/**
 * Rate Review API Service
 * Centralized service for all Rate Review API operations
 * @module RateReviewApiService
 */

import api from "../../../api/config";
import { ENDPOINTS } from "../../../api/endpoints";
import { DEFAULT_PAGE_SIZE } from "../../../utils/constants";
import { formatRateReviewLead } from "../utils/rateReviewHelpers";
import { getDateRangeFromPreset } from "../utils/datePresetHelpers";

/**
 * Builds request parameters from filters and pagination
 * Normalizes and validates filter values, trims strings, excludes empty filters
 * @param {Object} params - Request parameters
 * @param {number} params.page - Page number (defaults to 1)
 * @param {Object} params.filters - Filter object
 * @returns {Object} Request parameters for API
 * @private
 */
const buildRequestParams = ({ page, filters = {} }) => {
  const pageNumber = Math.max(1, parseInt(page, 10) || 1);
  
  const requestParams = {
    page: pageNumber,
    per_page: DEFAULT_PAGE_SIZE,
  };

  if (filters.searchValue?.trim()) {
    requestParams.searchValue = filters.searchValue.trim();
  }

  if (filters.phone?.trim()) {
    requestParams.phone = filters.phone.trim();
  }

  if (filters.gatewayId?.trim()) {
    requestParams.gatewayId = filters.gatewayId.trim();
  }

  if (filters.status && Array.isArray(filters.status) && filters.status.length > 0) {
    requestParams.status = filters.status;
  }

  if (filters.dateSubmitted) {
    let startDate = null;
    let endDate = null;

    if (filters.dateSubmitted.preset || filters.dateSubmitted.rangeType) {
      const preset = filters.dateSubmitted.preset || filters.dateSubmitted.rangeType;
      const dateRange = getDateRangeFromPreset(preset);
      startDate = dateRange.start;
      endDate = dateRange.end;
    } else if (filters.dateSubmitted.start || filters.dateSubmitted.startDate) {
      startDate = filters.dateSubmitted.start || filters.dateSubmitted.startDate;
      endDate = filters.dateSubmitted.end || filters.dateSubmitted.endDate;
    }

    if (startDate && endDate) {
      requestParams.createdDateStart = startDate;
      requestParams.createdDateEnd = endDate;
    }
  }

  return requestParams;
};

/**
 * Submits a new rate review form (public endpoint)
 * Accepts FormData with file uploads, creates lead in Zoho CRM
 * @param {FormData} formData - Form data with files
 * @returns {Promise<Object>} Response with success status and zohoLeadID
 * @throws {Error} If API request fails or validation errors occur
 */
export const submitRateReviewForm = async (formData) => {
  try {
    const response = await api.post(ENDPOINTS.SUBMIT_RATE_REVIEW, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    
    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }

    if (!response.data.success) {
      const errorMessage = response.data.error || "Failed to submit rate review form";
      throw new Error(errorMessage);
    }

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to submit rate review form: ${String(error)}`);
  }
};

/**
 * Fetches rate review leads with filters and pagination
 * @param {Object} params - Request parameters
 * @param {number} [params.page=1] - Page number
 * @param {Object} [params.filters={}] - Filter object
 * @returns {Promise<{leads: Array, hasMore: boolean, page: number}>} Leads data and pagination info
 * @throws {Error} If API request fails
 */
export const fetchRateReviewLeads = async ({ page = 1, filters = {} }) => {
  try {
    const requestParams = buildRequestParams({ page, filters });
    const response = await api.post(ENDPOINTS.GET_RATE_REVIEW_LEADS, requestParams);

    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }

    if (!response.data.success) {
      const errorMessage = response.data.error || "Failed to fetch leads";
      throw new Error(errorMessage);
    }

    const rawLeads = response.data.data || [];
    const formattedLeads = Array.isArray(rawLeads) 
      ? rawLeads.map(formatRateReviewLead)
      : [];

    const info = response.data.info || {};
    const currentPage = info.page || page;
    const totalPages = info.total_pages || 
      Math.ceil((info.count || formattedLeads.length) / DEFAULT_PAGE_SIZE);

    const hasMore = info.more_records === true || currentPage < totalPages;

    return {
      leads: formattedLeads,
      hasMore,
      page: currentPage,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to fetch leads: ${String(error)}`);
  }
};

/**
 * Combined search for rate review leads with multiple filters
 * Returns all matching results without pagination
 * @param {Object} filters - Filter object
 * @returns {Promise<{success: boolean, leads: Array, info: Object, appliedFilters: Object}>} Search results
 * @throws {Error} If API request fails
 */
export const searchRateReviewLeadsCombined = async (filters = {}) => {
  try {
    const searchParams = {};

    if (filters.searchValue?.trim()) {
      searchParams.searchValue = filters.searchValue.trim();
    }

    if (filters.phone?.trim()) {
      searchParams.phone = filters.phone.trim();
    }

    if (filters.gatewayId?.trim()) {
      searchParams.gatewayId = filters.gatewayId.trim();
    }

    if (filters.status && Array.isArray(filters.status) && filters.status.length > 0) {
      searchParams.status = filters.status;
    }

    if (filters.dateSubmitted) {
      searchParams.dateSubmitted = filters.dateSubmitted;
    }

    const response = await api.post(ENDPOINTS.SEARCH_RATE_REVIEW_COMBINED, searchParams);

    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }

    if (!response.data.success) {
      const errorMessage = response.data.error || "Failed to search leads";
      throw new Error(errorMessage);
    }

    const rawLeads = response.data.data || [];
    const formattedLeads = Array.isArray(rawLeads) 
      ? rawLeads.map(formatRateReviewLead)
      : [];

    return {
      success: true,
      leads: formattedLeads,
      info: response.data.info || { count: formattedLeads.length },
      appliedFilters: response.data.appliedFilters || {},
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to search leads: ${String(error)}`);
  }
};

/**
 * Updates a rate review lead
 * @param {Object} params - Update parameters
 * @param {string} params.leadId - Lead ID (Zoho ID) - required
 * @param {Object} params.updates - Update fields object - required
 * @returns {Promise<Object>} Updated lead data from API response
 * @throws {Error} If leadId is missing, updates is empty, or API request fails
 */
export const updateRateReviewLead = async ({ leadId, updates }) => {
  if (!leadId || typeof leadId !== 'string') {
    throw new Error("Lead ID is required and must be a string");
  }

  if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
    throw new Error("Updates object is required and must not be empty");
  }

  try {
    const response = await api.put(ENDPOINTS.UPDATE_RATE_REVIEW_LEAD(leadId), {
      zohoId: leadId,
      updates,
    });

    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }

    if (!response.data.success) {
      const errorMessage = response.data.error || "Failed to update lead";
      throw new Error(errorMessage);
    }

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to update lead: ${String(error)}`);
  }
};

/**
 * Gets a rate review lead by Zoho ID (public access for status page)
 * @param {string} zohoId - Zoho lead ID from URL parameter
 * @returns {Promise<Object>} Lead data from API response
 * @throws {Error} If zohoId is invalid, lead not found, or not a Rate Review lead
 */
export const getRateReviewLeadByToken = async (zohoId) => {
  if (!zohoId || typeof zohoId !== 'string') {
    throw new Error("Zoho ID is required and must be a string");
  }

  try {
    const response = await api.get(ENDPOINTS.GET_RATE_REVIEW_BY_TOKEN(zohoId));

    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }

    if (!response.data.success) {
      const errorMessage = response.data.error || "Failed to get lead";
      throw new Error(errorMessage);
    }

    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to get lead: ${String(error)}`);
  }
};

/**
 * Gets attachments for a rate review lead
 * Returns empty array if no attachments or on error (graceful degradation)
 * @param {string} zohoId - Zoho lead ID
 * @returns {Promise<Object>} Attachments data from API response
 */
export const getRateReviewAttachments = async (zohoId) => {
  if (!zohoId || typeof zohoId !== 'string') {
    throw new Error("Zoho ID is required and must be a string");
  }

  try {
    const response = await api.get(ENDPOINTS.GET_LEAD_ATTACHMENTS(zohoId));

    if (!response || !response.data) {
      throw new Error("Invalid response from server");
    }

    return {
      success: true,
      data: response.data.data || [],
    };
  } catch (error) {
    return {
      success: true,
      data: [],
    };
  }
};

/**
 * Processes form values for lead update
 * Removes undefined/null values, trims strings, normalizes special fields
 * @param {Object} values - Form values from the update modal
 * @returns {Object} Processed updates object ready for API submission
 */
export const processLeadUpdateValues = (values) => {
  if (!values || typeof values !== 'object') {
    return {};
  }

  const updates = Object.entries(values).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc;
    }

    if (key === 'Country_of_Formation') {
      if (value) {
        acc[key] = Array.isArray(value) ? value : [value];
      }
      return acc;
    }

    if (typeof value === 'string') {
      acc[key] = value.trim();
    } else {
      acc[key] = value;
    }

    return acc;
  }, {});

  return updates;
};

/**
 * Default export - Service object with all methods
 */
const rateReviewApiService = {
  submitRateReviewForm,
  fetchRateReviewLeads,
  searchRateReviewLeadsCombined,
  updateRateReviewLead,
  getRateReviewLeadByToken,
  getRateReviewAttachments,
  processLeadUpdateValues,
};

export default rateReviewApiService;
